import { useEffect, useMemo, useRef, useState } from 'react';
import {
  FolderTree,
  ImageIcon,
  Loader2,
  Package,
  Pencil,
  Plus,
  Search,
  Tag,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import { ApiError } from '../../lib/api';
import { createCategory, deleteCategory, getCategories } from '../../services/categories';
import {
  createProduct,
  deleteProduct,
  getAdminProduct,
  getProducts,
  updateProduct,
} from '../../services/products';
import type { Category } from '../../types/category';
import type { ProductCard, ProductDetail } from '../../types/product';
import {
  PRODUCT_IMAGE_ACCEPT,
  PRODUCT_IMAGE_MAX_BYTES,
  validateProductImageFile,
} from '../../utils/productImageUpload';

const GALLERY_MAX_FILES = 10;

interface ProductFormState {
  category_id: number;
  name: string;
  description: string;
  price: string;
  sale_price: string;
  stock: string;
  is_active: boolean;
  imageUrl: string | null;
  imageFile?: File;
  galleryUrls: string[];
  galleryFiles: File[];
  replaceGallery: boolean;
}

function emptyProductForm(categoryId: number): ProductFormState {
  return {
    category_id: categoryId,
    name: '',
    description: '',
    price: '',
    sale_price: '',
    stock: '0',
    is_active: true,
    imageUrl: null,
    galleryUrls: [],
    galleryFiles: [],
    replaceGallery: false,
  };
}

function productDetailToForm(detail: ProductDetail): ProductFormState {
  return {
    category_id: detail.category.id,
    name: detail.name,
    description: detail.description ?? '',
    price: String(detail.price),
    sale_price: detail.sale_price != null ? String(detail.sale_price) : '',
    stock: String(detail.stock),
    is_active: detail.is_active,
    imageUrl: detail.image,
    galleryUrls: detail.gallery ?? [],
    galleryFiles: [],
    replaceGallery: false,
  };
}

function parseNonNegativeNumber(value: string): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
      />
    </label>
  );
}

function StatusBadge({ isActive }: { isActive: boolean }) {
  return (
    <span
      className={`inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
        isActive
          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
          : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'
      }`}
    >
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );
}

function pickImageFile(file: File | undefined, onPick: (file: File) => void): boolean {
  if (!file) return false;
  const error = validateProductImageFile(file);
  if (error) {
    toast.error(error);
    return false;
  }
  if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
    toast.error('Image must be 4 MB or smaller');
    return false;
  }
  onPick(file);
  return true;
}

const CATEGORY_IMAGE_MAX_BYTES = 4 * 1024 * 1024;

function CategoriesModal({
  open,
  onClose,
  categories,
  categoryName,
  categoryIsActive,
  categoryImageFile,
  saving,
  onCategoryNameChange,
  onCategoryIsActiveChange,
  onCategoryImageChange,
  onCreateCategory,
  onRemoveCategory,
}: {
  open: boolean;
  onClose: () => void;
  categories: Category[];
  categoryName: string;
  categoryIsActive: boolean;
  categoryImageFile: File | undefined;
  saving: boolean;
  onCategoryNameChange: (value: string) => void;
  onCategoryIsActiveChange: (value: boolean) => void;
  onCategoryImageChange: (file: File | undefined) => void;
  onCreateCategory: (e: React.FormEvent) => void;
  onRemoveCategory: (id: number) => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close categories modal"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="categories-modal-title"
        className="relative z-10 w-full max-w-lg max-h-[85vh] overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl flex flex-col"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500">
              <FolderTree className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 id="categories-modal-title" className="text-base font-semibold text-slate-900">
                Categories
              </h2>
              <p className="text-xs text-slate-500">{categories.length} total</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-5">
          <form onSubmit={onCreateCategory} className="space-y-3 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-sm font-semibold text-slate-800">New category</p>
            <Field
              label="Category name"
              value={categoryName}
              onChange={onCategoryNameChange}
              placeholder="e.g. Gaming"
              required
            />
            <label className="block">
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Image</span>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) {
                    onCategoryImageChange(undefined);
                    return;
                  }
                  if (file.size > CATEGORY_IMAGE_MAX_BYTES) {
                    toast.error('Image must be 4 MB or smaller');
                    e.target.value = '';
                    return;
                  }
                  onCategoryImageChange(file);
                }}
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700"
              />
              {categoryImageFile && (
                <p className="mt-1 truncate text-xs text-indigo-600">{categoryImageFile.name}</p>
              )}
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={categoryIsActive}
                onChange={(e) => onCategoryIsActiveChange(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              Active on storefront
            </label>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {saving ? 'Creating…' : 'Create category'}
            </button>
          </form>

          <div>
            <p className="mb-3 text-sm font-semibold text-slate-800">Current categories</p>
            {categories.length === 0 ? (
              <p className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-500">
                No categories yet. Create one above.
              </p>
            ) : (
              <ul className="space-y-2">
                {categories.map((category) => (
                  <li
                    key={category.id}
                    className="flex items-start justify-between gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2.5"
                  >
                    <div className="flex min-w-0 gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                        {category.image ? (
                          <img
                            src={category.image}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <FolderTree className="h-5 w-5 text-slate-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900">{category.name}</p>
                        <p className="mt-0.5 text-xs text-slate-500">{category.slug}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-2">
                          <span
                            className={`inline-flex rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                              category.is_active
                                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                                : 'bg-slate-100 text-slate-500 ring-1 ring-slate-200'
                            }`}
                          >
                            {category.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
                            {category.products_count ?? 0} products
                          </span>
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => onRemoveCategory(category.id)}
                      disabled={saving}
                      className="shrink-0 rounded-lg p-1.5 text-red-400 transition hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      aria-label={`Delete ${category.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductModal({
  open,
  form,
  categories,
  isNew,
  saving,
  loadingDetail,
  onClose,
  onChange,
  onSave,
}: {
  open: boolean;
  form: ProductFormState | null;
  categories: Category[];
  isNew: boolean;
  saving: boolean;
  loadingDetail: boolean;
  onClose: () => void;
  onChange: (patch: Partial<ProductFormState>) => void;
  onSave: (e: React.FormEvent) => void;
}) {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const imagePreview = useMemo(() => {
    if (form?.imageFile) return URL.createObjectURL(form.imageFile);
    return form?.imageUrl ?? null;
  }, [form?.imageFile, form?.imageUrl]);

  useEffect(() => {
    if (!form?.imageFile || !imagePreview?.startsWith('blob:')) return;
    return () => URL.revokeObjectURL(imagePreview);
  }, [form?.imageFile, imagePreview]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open || !form) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
        aria-label="Close product modal"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="product-modal-title"
        className="relative z-10 flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500">
              <Package className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 id="product-modal-title" className="text-base font-semibold text-slate-900">
                {isNew ? 'Add product' : 'Edit product'}
              </h2>
              <p className="text-xs text-slate-500">
                {isNew ? 'Create a new catalog item' : 'Update product details'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loadingDetail ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
          </div>
        ) : (
          <form onSubmit={onSave} className="flex flex-1 flex-col overflow-hidden">
            <div className="overflow-y-auto flex-1 p-5">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label="Product name"
                  value={form.name}
                  onChange={(v) => onChange({ name: v })}
                  required
                />
                <Field
                  label="Price"
                  type="number"
                  value={form.price}
                  onChange={(v) => onChange({ price: v })}
                />
                <Field
                  label="Sale price"
                  type="number"
                  value={form.sale_price}
                  onChange={(v) => onChange({ sale_price: v })}
                  placeholder="Optional"
                />
                <Field
                  label="Stock"
                  type="number"
                  value={form.stock}
                  onChange={(v) => onChange({ stock: v })}
                />
                <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => onChange({ is_active: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Active on storefront
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">Category</span>
                  <select
                    value={form.category_id}
                    onChange={(e) => onChange({ category_id: Number(e.target.value) })}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    required
                  >
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">
                    Main image {isNew && <span className="text-red-500">*</span>}
                  </span>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
                      <div className="h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-white">
                        {imagePreview ? (
                          <img src={imagePreview} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center text-slate-400">
                            <ImageIcon className="h-6 w-6" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => imageInputRef.current?.click()}
                          className="inline-flex w-fit items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700"
                        >
                          <Upload className="h-4 w-4" />
                          {form.imageFile || form.imageUrl ? 'Replace image' : 'Choose image'}
                        </button>
                        <p className="text-xs text-slate-500">
                          JPEG, PNG, WebP or GIF · Max 4 MB · Max 2000×2000px
                        </p>
                        {form.imageFile && (
                          <p className="truncate text-xs text-indigo-600">{form.imageFile.name}</p>
                        )}
                      </div>
                    </div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept={PRODUCT_IMAGE_ACCEPT}
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (pickImageFile(file, (picked) => onChange({ imageFile: picked }))) {
                          e.target.value = '';
                        }
                      }}
                    />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">Gallery</span>
                  <p className="mb-2 text-xs text-slate-500">
                    Up to {GALLERY_MAX_FILES} images, 4 MB each. Uploading new gallery images
                    replaces the entire gallery when saving.
                  </p>
                  {(form.galleryUrls.length > 0 || form.galleryFiles.length > 0) && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {form.galleryUrls.map((url) => (
                        <img
                          key={url}
                          src={url}
                          alt=""
                          className="h-14 w-14 rounded-lg border border-slate-200 object-cover"
                        />
                      ))}
                      {form.galleryFiles.map((file) => (
                        <div
                          key={file.name}
                          className="flex h-14 w-14 items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-1 text-[10px] font-medium text-indigo-700 text-center"
                        >
                          New
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => galleryInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:text-indigo-700"
                  >
                    <Upload className="h-4 w-4" />
                    Add gallery images
                  </button>
                  <input
                    ref={galleryInputRef}
                    type="file"
                    accept={PRODUCT_IMAGE_ACCEPT}
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files ?? []);
                      const valid: File[] = [];
                      for (const file of files) {
                        if (valid.length >= GALLERY_MAX_FILES) {
                          toast.error(`Gallery allows up to ${GALLERY_MAX_FILES} images`);
                          break;
                        }
                        if (pickImageFile(file, (picked) => valid.push(picked))) {
                          /* added */
                        }
                      }
                      if (valid.length) {
                        onChange({
                          galleryFiles: [...form.galleryFiles, ...valid].slice(0, GALLERY_MAX_FILES),
                          replaceGallery: true,
                        });
                      }
                      e.target.value = '';
                    }}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block">
                    <span className="mb-1.5 block text-sm font-medium text-slate-700">Description</span>
                    <textarea
                      rows={3}
                      value={form.description}
                      onChange={(e) => onChange({ description: e.target.value })}
                      className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-200/50 transition hover:from-indigo-700 hover:to-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                {saving ? 'Saving…' : isNew ? 'Add product' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

const Products = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryName, setCategoryName] = useState('');
  const [categoryIsActive, setCategoryIsActive] = useState(true);
  const [categoryImageFile, setCategoryImageFile] = useState<File | undefined>();
  const [categorySaving, setCategorySaving] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [productModal, setProductModal] = useState<{
    form: ProductFormState;
    productId?: number;
    isNew: boolean;
  } | null>(null);
  const [productSaving, setProductSaving] = useState(false);
  const [productDetailLoading, setProductDetailLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [cats, prods] = await Promise.all([getCategories(), getProducts()]);
        if (!cancelled) {
          setCategories(cats);
          setProducts(prods);
        }
      } catch {
        if (!cancelled) toast.error('Failed to load catalog');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const reloadCatalog = async () => {
    const [cats, prods] = await Promise.all([getCategories(), getProducts()]);
    setCategories(cats);
    setProducts(prods);
  };

  const filteredProducts = useMemo(() => {
    let result = products;

    if (selectedCategoryId !== 'all') {
      result = result.filter((p) => String(p.category.id) === selectedCategoryId);
    }

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((p) => {
        const catName = p.category.name;
        return (
          p.name.toLowerCase().includes(query) ||
          p.slug.toLowerCase().includes(query) ||
          catName.toLowerCase().includes(query)
        );
      });
    }

    return result;
  }, [products, selectedCategoryId, searchQuery]);

  const hasActiveFilters = selectedCategoryId !== 'all' || searchQuery.trim().length > 0;

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = categoryName.trim();
    if (!trimmed) {
      toast.error('Category name is required');
      return;
    }

    setCategorySaving(true);
    try {
      const created = await createCategory({
        name: trimmed,
        is_active: categoryIsActive,
        image: categoryImageFile,
      });
      setCategories((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
      setCategoryName('');
      setCategoryIsActive(true);
      setCategoryImageFile(undefined);
      toast.success('Category created');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create category';
      toast.error(message);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleAddProduct = () => {
    if (categories.length === 0) {
      setCategoriesModalOpen(true);
      toast.error('Create a category first');
      return;
    }
    const defaultCategoryId =
      selectedCategoryId !== 'all'
        ? Number(selectedCategoryId)
        : categories[0].id;
    setProductModal({ form: emptyProductForm(defaultCategoryId), isNew: true });
  };

  const handleEditProduct = async (product: ProductCard) => {
    setProductModal({
      form: emptyProductForm(product.category.id),
      productId: product.id,
      isNew: false,
    });
    setProductDetailLoading(true);
    try {
      const detail = await getAdminProduct(product.id);
      setProductModal({
        form: productDetailToForm(detail),
        productId: detail.id,
        isNew: false,
      });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load product';
      toast.error(message);
      setProductModal(null);
    } finally {
      setProductDetailLoading(false);
    }
  };

  const handleCloseProductModal = () => {
    setProductModal(null);
  };

  const handleProductModalChange = (patch: Partial<ProductFormState>) => {
    setProductModal((prev) => (prev ? { ...prev, form: { ...prev.form, ...patch } } : null));
  };

  const handleSaveProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!productModal) return;

    const { form, isNew, productId } = productModal;
    const name = form.name.trim();
    const price = parseNonNegativeNumber(form.price);
    const stock = parseNonNegativeNumber(form.stock);
    const salePriceRaw = form.sale_price.trim();
    const salePrice = salePriceRaw === '' ? null : parseNonNegativeNumber(salePriceRaw);

    if (!name || price === null || stock === null || !form.category_id) {
      toast.error('Name, category, price, and stock are required');
      return;
    }
    if (salePriceRaw !== '' && salePrice === null) {
      toast.error('Sale price must be a valid number');
      return;
    }
    if (salePrice !== null && salePrice >= price) {
      toast.error('Sale price must be less than the regular price');
      return;
    }
    if (isNew && !form.imageFile) {
      toast.error('Main product image is required');
      return;
    }

    setProductSaving(true);
    try {
      if (isNew) {
        await createProduct({
          category_id: form.category_id,
          name,
          description: form.description.trim() || undefined,
          price,
          sale_price: salePrice,
          stock: Math.floor(stock),
          is_active: form.is_active,
          image: form.imageFile!,
          gallery: form.galleryFiles.length ? form.galleryFiles : undefined,
        });
        toast.success('Product created');
      } else if (productId !== undefined) {
        await updateProduct(productId, {
          category_id: form.category_id,
          name,
          description: form.description.trim(),
          price,
          sale_price: salePrice,
          stock: Math.floor(stock),
          is_active: form.is_active,
          image: form.imageFile,
          gallery:
            form.replaceGallery && form.galleryFiles.length > 0
              ? form.galleryFiles
              : undefined,
        });
        toast.success('Product updated');
      }
      await reloadCatalog();
      setProductModal(null);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to save product';
      toast.error(message);
    } finally {
      setProductSaving(false);
    }
  };

  const handleRemoveCategory = async (id: number) => {
    const category = categories.find((c) => c.id === id);
    if (!category) return;

    const count = category.products_count ?? 0;
    if (
      count > 0 &&
      !window.confirm(
        `Delete "${category.name}" and all ${count} product(s) in this category? This cannot be undone.`,
      )
    ) {
      return;
    }

    setCategorySaving(true);
    try {
      await deleteCategory(id);
      if (selectedCategoryId === String(id)) setSelectedCategoryId('all');
      await reloadCatalog();
      toast.success('Category deleted');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete category';
      toast.error(message);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleRemoveProduct = async (id: number) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;

    try {
      await deleteProduct(id);
      if (productModal?.productId === id) setProductModal(null);
      await reloadCatalog();
      toast.success('Product deleted');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete product';
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col w-full">
      <div className="flex flex-1 w-full">
        <Sidebar />
        <main className="flex-1 p-4 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="mb-6 md:mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-xs font-medium text-indigo-700 mb-3">
                <Package className="h-3.5 w-3.5" />
                Catalog management
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                Products
              </h1>
              <p className="mt-1.5 text-sm md:text-base text-slate-500">
                Manage your product catalog.
              </p>
            </div>
            {!loading && (
              <div className="flex shrink-0 flex-wrap items-center gap-2 self-start">
                <button
                  type="button"
                  onClick={() => setCategoriesModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-amber-200 hover:bg-amber-50 hover:text-amber-800"
                >
                  <FolderTree className="h-4 w-4" />
                  Manage Categories
                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">
                    {categories.length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={handleAddProduct}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-200/50 transition hover:from-indigo-700 hover:to-violet-700"
                >
                  <Plus className="h-4 w-4" />
                  Add product
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-500 shadow-sm ring-4 ring-indigo-100">
                    <Tag className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">Product list</h2>
                    <p className="text-sm text-slate-500">{filteredProducts.length} items shown</p>
                  </div>
                </div>

                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                >
                  <option value="all">All categories</option>
                  {categories.map((c) => (
                    <option key={c.id} value={String(c.id)}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative mb-4">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by name, description, or category…"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/80 py-2.5 pl-10 pr-10 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {categories.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center">
                  <FolderTree className="mx-auto h-10 w-10 text-slate-300" />
                  <p className="mt-3 text-sm font-medium text-slate-700">Create a category first</p>
                  <p className="mt-1 text-sm text-slate-500">Products must belong to a category.</p>
                  <button
                    type="button"
                    onClick={() => setCategoriesModalOpen(true)}
                    className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    <FolderTree className="h-4 w-4" />
                    Manage categories
                  </button>
                </div>
              ) : filteredProducts.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center">
                    {hasActiveFilters ? (
                      <>
                        <Search className="mx-auto h-10 w-10 text-slate-300" />
                        <p className="mt-3 text-sm font-medium text-slate-700">No matching products</p>
                        <p className="mt-1 text-sm text-slate-500">
                          Try a different search or clear your filters.
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setSearchQuery('');
                            setSelectedCategoryId('all');
                          }}
                          className="mt-4 inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-indigo-200 hover:text-indigo-700"
                        >
                          Clear filters
                        </button>
                      </>
                    ) : (
                      <>
                        <Package className="mx-auto h-10 w-10 text-slate-300" />
                        <p className="mt-3 text-sm font-medium text-slate-700">No products yet</p>
                        <button
                          type="button"
                          onClick={handleAddProduct}
                          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                        >
                          <Plus className="h-4 w-4" />
                          Add product
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredProducts.map((product) => (
                      <article
                        key={product.id}
                        className="rounded-xl border border-slate-200 overflow-hidden"
                      >
                        <div className="flex items-center gap-3 p-3 bg-slate-50/50">
                          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-white border border-slate-200">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-slate-400">
                                <ImageIcon className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="truncate text-sm font-semibold text-slate-900">
                                {product.name || 'Untitled product'}
                              </p>
                              <StatusBadge isActive={product.is_active} />
                            </div>
                            <p className="text-xs text-slate-500">
                              {product.category.name} ·{' '}
                              <span className="font-medium text-slate-700">
                                {product.sale_price != null ? (
                                  <>
                                    <span className="line-through text-slate-400">
                                      {product.price.toFixed(2)}
                                    </span>{' '}
                                    {product.sale_price.toFixed(2)}
                                  </>
                                ) : (
                                  product.price.toFixed(2)
                                )}
                              </span>
                              {' · '}
                              <span className="font-medium text-slate-700">
                                Stock {product.stock}
                              </span>
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => void handleEditProduct(product)}
                            className="rounded-lg p-2 text-indigo-600 transition hover:bg-indigo-50 hover:text-indigo-700"
                            aria-label="Edit product"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleRemoveProduct(product.id)}
                            className="rounded-lg p-2 text-red-400 transition hover:bg-red-50 hover:text-red-600"
                            aria-label="Remove product"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
            </section>
          )}

          <CategoriesModal
            open={categoriesModalOpen}
            onClose={() => setCategoriesModalOpen(false)}
            categories={categories}
            categoryName={categoryName}
            categoryIsActive={categoryIsActive}
            categoryImageFile={categoryImageFile}
            saving={categorySaving}
            onCategoryNameChange={setCategoryName}
            onCategoryIsActiveChange={setCategoryIsActive}
            onCategoryImageChange={setCategoryImageFile}
            onCreateCategory={handleCreateCategory}
            onRemoveCategory={handleRemoveCategory}
          />

          <ProductModal
            open={productModal !== null}
            form={productModal?.form ?? null}
            categories={categories}
            isNew={productModal?.isNew ?? false}
            saving={productSaving}
            loadingDetail={productDetailLoading}
            onClose={handleCloseProductModal}
            onChange={handleProductModalChange}
            onSave={handleSaveProduct}
          />
        </main>
      </div>
    </div>
  );
};

export default Products;
